import React, { useEffect, useMemo, useState } from "react";
import {
  Box,
  Container,
  Typography,
  List,
  ListItem,
  ListItemText,
  Divider,
  Paper,
  AppBar,
  Toolbar,
  Button,
  Drawer,
  IconButton,
  useTheme,
  useMediaQuery,
} from "@mui/material";
import MenuIcon from "@mui/icons-material/Menu";
import BannerSection from "../../components/common/BannerSection";
import { Images } from "../../utils/Images";
import { useDispatch, useSelector } from "react-redux";
import { fetchCmsHomeAction } from "../../store/slices/cmsSlice";

const termsAndConditions = () => {
  const [mobileOpen, setMobileOpen] = useState(false);
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down("md"));
  const dispatch = useDispatch();
  const cmsHome = useSelector((state) => state.cms.home);

  const handleDrawerToggle = () => {
    setMobileOpen(!mobileOpen);
  };

  useEffect(() => {
    if (!cmsHome) {
      dispatch(fetchCmsHomeAction());
    }
  }, [cmsHome, dispatch]);

  const { sections, baseTitle } = useMemo(() => {
    const data = Array.isArray(cmsHome?.data) ? cmsHome.data : [];
    const section = data.find((s) => s?.id === 71);
    const related = Array.isArray(section?.relatedCMS) ? section.relatedCMS : [];
    const toSlug = (str = "") =>
      String(str)
        .toLowerCase()
        .replace(/[^a-z0-9\s-]/g, "")
        .trim()
        .replace(/\s+/g, "-");
    return {
      baseTitle: section?.title || "",
      sections: related
        .filter((r) => r && (r.status === 1 || r.status === true))
        .map((r) => ({ id: toSlug(r.title), title: r.title || "", descriptionHtml: r.description || "" })),
    };
  }, [cmsHome]);

  const drawer = (
    <Box
      sx={{
        width: 310,
        p: "20px",
        border: "1px solid #F1F1F1",
        borderRadius: "12px",
      }}
    >
      <Typography
        variant="h6"
        sx={{
          borderBottom: "1px solid #B9B9B8",
          fontWeight: "700",
          fontSize: "18px",
          lineHeight: "24px",
          color: "#1D1D1B",
          paddingBottom: "16px",
          display: "flex",
          gap: "8px",
        }}
      >
        <img
          src={Images.streamlineSharpStar}
          alt="Images.streamlineSharpStar"
        />
        Table of Contents
      </Typography>
      <List>
        {sections.map((section, index) => (
          <ListItem
            key={section.id}
            component="a"
            href={`#${section.id}`}
            onClick={isMobile ? handleDrawerToggle : undefined}
            sx={{
              color: "inherit",
              textDecoration: "none",
              fontWeight: "500",
              letterSpacing: " -0.14px",
              fontSize: "14px",
              padding: " 0",
              marginBottom: "24px",
              "&:hover": { textDecoration: "none", color: "inherit" },
              "&:focus": { outline: "none" },
              "&:active": { color: "inherit" }
            }}
          >
            <ListItemText
              sx={{
                fontSize: "14px",
                lineHeight: "22px",
                color: "inherit",
                fontWeight: "500",
                m: "0px",
                "& .MuiListItemText-primary": {
                  fontSize: "14px",
                  lineHeight: "22px",
                  color: "inherit",
                  fontWeight: "500",
                  mb: "0px",
                },
              }}
              primary={`${index + 1}. ${section.title}`}
            />
          </ListItem>
        ))}
      </List>
    </Box>
  );

  return (
    <Box sx={{ backgroundColor: "#fff" }}>
      <Box sx={{ mb: isMobile ? "10px" : "40px", mt: isMobile ? "0" : "30px" }}>
        <BannerSection title={baseTitle} subtitle="" />
      </Box>
      <Box
        sx={{
          display: "flex",
          flexDirection: "column",
          maxWidth: "1344px",
          mx: "auto",
          px: "16px",
        }}
      >
        <Box sx={{ display: "flex", flexGrow: 1, gap: "14px" }}>
          {/* Sidebar Drawer */}
          <Drawer
            variant="temporary"
            open={mobileOpen}
            onClose={handleDrawerToggle}
            ModalProps={{ keepMounted: true }}
            sx={{
              display: { xs: "block", md: "none" },
              "& .MuiDrawer-paper": { boxSizing: "border-box", width: 250 },
            }}
          >
            {drawer}
          </Drawer>

          <Box
            sx={{
              display: { xs: "none", md: "block" },
              flex: "0 0 auto",
              width: "310px",
              position: "sticky",
              top: "0px",
              alignSelf: "flex-start",
              height: "fit-content",
              maxHeight: "100%",
              overflowY: "auto",
            }}
          >
            {drawer}
          </Box>

          {/* Main Content */}
          <Box
            sx={{
              flexGrow: 1,
              border: "1px solid #F1F1F1",
              p: "20px",
              borderRadius: "12px",
              backgroundColor: "#FFFFFF",
            }}
          >
            <Paper elevation={0} sx={{ borderRadius: 2 }}>
              {sections.map((sec) => (
                <Box key={sec.id} id={sec.id} sx={{ mb: "20px" }}>
                  <Typography
                    variant="h5"
                    gutterBottom
                    sx={{
                      fontWeight: "700",
                      color: "#1D1D1B",
                      mb: "10px",
                      fontSize: "16px",
                      lineHeight: "24px",
                    }}
                  >
                    {sec.title}
                  </Typography>
                  <Box
                    sx={{ color: "#4A4A49", fontSize: "14px", lineHeight: "22px", '& *': { color: '#4A4A49 !important' }, '& ul': { listStyle: 'disc', paddingLeft: '20px', marginTop: '8px', marginBottom: '8px' }, '& li': { listStyle: 'disc', display: 'list-item' } }}
                    dangerouslySetInnerHTML={{ __html: sec.descriptionHtml || "" }}
                  />
                </Box>
              ))}
            </Paper>
          </Box>
        </Box>

        {/* Footer */}
      </Box>

    </Box>
  );
};

export default termsAndConditions;
